import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  useMetricsUi,
  headCellSx,
  numCellSx,
  textCellSx,
  SectionHead,
} from "./metricsUi";
import {
  formatMetricCell,
  formatCurrency,
  formatCount,
  dayOfWeekLabel,
  shortDateLabel,
} from "./metricsFormat";

/**
 * Period-local week rollups with expandable nested daily rows
 * (reference week-row → day detail hierarchy). Weeks come from the API's
 * week_history; nested days are the existing daily series filtered by the
 * week's date range — display only.
 */
const FiscalWeekHistoryTable = ({
  weekHistory = [],
  daily = [],
  selectedWeekId,
  onSelectWeek,
}) => {
  const ui = useMetricsUi();
  const [openWeeks, setOpenWeeks] = useState({});

  if (!weekHistory.length) return null;

  const toggleWeek = (weekId) =>
    setOpenWeeks((prev) => ({ ...prev, [weekId]: !prev[weekId] }));

  const daysForWeek = (week) =>
    daily.filter(
      (row) => row.date >= week.start_date && row.date <= week.end_date,
    );

  return (
    <Box sx={{ mt: 3 }}>
      <SectionHead
        title="Fiscal week performance"
        subtitle="7-day blocks within the selected fiscal period. Expand a week for its daily breakdown, or open it in the Daily tab."
      />

      <TableContainer sx={{ ...ui.card, overflow: "hidden" }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx(ui)}>Week</TableCell>
              <TableCell sx={headCellSx(ui)}>Date range</TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Gross Sales
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Orders
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Units Sold
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                UPT
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Gross AOV
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })} />
            </TableRow>
          </TableHead>
          <TableBody>
            {weekHistory.map((row) => {
              const open = !!openWeeks[row.week_id];
              const selected = row.week_id === selectedWeekId;
              const days = open ? daysForWeek(row) : [];
              return (
                <React.Fragment key={row.week_id}>
                  <TableRow
                    hover
                    onClick={() => toggleWeek(row.week_id)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: selected ? ui.accentSoft : "transparent",
                      "&:hover td": { backgroundColor: ui.surfaceAlt },
                    }}
                  >
                    <TableCell sx={textCellSx(ui)}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <ChevronRightIcon
                          sx={{
                            fontSize: 16,
                            color: ui.colors.textSecondary,
                            transition: "transform .15s ease",
                            transform: open ? "rotate(90deg)" : "none",
                          }}
                        />
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          {row.label}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={textCellSx(ui)}>
                      <Typography
                        sx={{ fontSize: 11.5, color: ui.colors.textTertiary }}
                      >
                        {row.start_date} – {row.end_date}
                      </Typography>
                    </TableCell>
                    <TableCell sx={numCellSx(ui, { fontWeight: 600 })}>
                      {formatMetricCell(row.gross_sales, "currency")}
                    </TableCell>
                    <TableCell sx={numCellSx(ui)}>
                      {formatMetricCell(row.orders, "count")}
                    </TableCell>
                    <TableCell sx={numCellSx(ui)}>
                      {formatMetricCell(row.units_sold, "count")}
                    </TableCell>
                    <TableCell sx={numCellSx(ui)}>
                      {formatMetricCell(row.upt, "ratio")}
                    </TableCell>
                    <TableCell sx={numCellSx(ui)}>
                      {formatMetricCell(row.gross_aov, "currency2")}
                    </TableCell>
                    <TableCell sx={numCellSx(ui, { py: 0.5 })}>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectWeek?.(row);
                        }}
                        sx={{
                          fontSize: 11,
                          textTransform: "none",
                          color: ui.accent,
                          minWidth: 0,
                          px: 1,
                        }}
                      >
                        Daily →
                      </Button>
                    </TableCell>
                  </TableRow>

                  {open && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        sx={{
                          p: 0,
                          borderBottom: `1px solid ${ui.colors.border}`,
                          backgroundColor: ui.surfaceAlt,
                        }}
                      >
                        <Box sx={{ px: 2, py: 1, pl: 4.5 }}>
                          {days.length ? (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={headCellSx(ui, { backgroundColor: "transparent" })}>
                                    Date
                                  </TableCell>
                                  <TableCell sx={headCellSx(ui, { backgroundColor: "transparent" })}>
                                    Day
                                  </TableCell>
                                  <TableCell sx={headCellSx(ui, { backgroundColor: "transparent", textAlign: "right" })}>
                                    Gross Sales
                                  </TableCell>
                                  <TableCell sx={headCellSx(ui, { backgroundColor: "transparent", textAlign: "right" })}>
                                    Orders
                                  </TableCell>
                                  <TableCell sx={headCellSx(ui, { backgroundColor: "transparent", textAlign: "right" })}>
                                    Units Sold
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {days.map((day) => (
                                  <TableRow key={day.date}>
                                    <TableCell
                                      sx={textCellSx(ui, {
                                        fontVariantNumeric: "tabular-nums",
                                        py: 0.75,
                                      })}
                                    >
                                      {shortDateLabel(day.date)}
                                    </TableCell>
                                    <TableCell
                                      sx={textCellSx(ui, {
                                        fontSize: 11,
                                        color: ui.colors.textTertiary,
                                        py: 0.75,
                                      })}
                                    >
                                      {dayOfWeekLabel(day.date)}
                                    </TableCell>
                                    <TableCell sx={numCellSx(ui, { py: 0.75 })}>
                                      {formatCurrency(day.gross_sales)}
                                    </TableCell>
                                    <TableCell sx={numCellSx(ui, { py: 0.75 })}>
                                      {formatCount(day.orders)}
                                    </TableCell>
                                    <TableCell sx={numCellSx(ui, { py: 0.75 })}>
                                      {formatCount(day.units_sold)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: ui.colors.textTertiary,
                                py: 1,
                              }}
                            >
                              No daily rows for this week
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FiscalWeekHistoryTable;
